import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import UserEditForm from './UserEditForm';
import { defaultWebUserModel, WebUserModel } from 'src/types/WebUserModel';
import api from '../../api/Api';
import ConfirmDelete from '../../components/controls/ConfirmDelete';
import { Box, CircularProgress } from '@mui/material';
import PageContainerLayout from 'src/pages/PageContainerLayout';
import { Logger } from 'src/utils/logger';
import useSnackbar from 'src/hooks/useSnackbar';

const WebUsers: FC<React.PropsWithChildren<unknown>> = () => {
  const { customerNumber } = useParams();
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<WebUserModel>({
    ...defaultWebUserModel,
  });
  const [data, setData] = useState<WebUserModel[]>([]);
  const { openSnackBar } = useSnackbar();

  const loadUsers = async () => {
    setIsLoading(true);
    let webUsers: WebUserModel[] = await api.get<WebUserModel[]>(`/user/companyusersbycustomer?customerNumber=${encodeURIComponent(customerNumber)}`);
    setData(webUsers);
    setIsLoading(false);
    setOpenForm(false);
    setOpenDelete(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const enterEdit = (item: any) => {
    setOpenForm(true);
    setEditUser(item);
  };

  const confirmDelete = (item: any) => {
    setOpenDelete(true);
    setEditUser(item);
  };

  const redirectToSCProfile = (user: WebUserModel) => {
    const baseBFGReportUrl = import.meta.env.REACT_APP_BFG_REPORT_URL;

    let url = `${baseBFGReportUrl}/MyAccount/Profile?companyUserId=${encodeURIComponent(user.companyUserId)}`;
    window.open(url);
  };

  const handleDelete = async (event: WebUserModel) => {
    try {
      await api.delete(`/user/deletewebuser?companyUserId=${encodeURIComponent(event.companyUserId)}`);
      openSnackBar('User deleted successfully');
      loadUsers();
    } catch (e) {
      Logger.error(e);
    }
  };

  const handleCancel = () => {
    setOpenForm(false);
    setOpenDelete(false);
  };

  interface EditCommandCellProps extends GridCellProps {
    enterEdit: (item: WebUserModel) => void;
    redirectToSCProfile: (item: WebUserModel) => void;
    confirmDelete: (item: WebUserModel) => void;
  }

  const EditCommandCell = (props: EditCommandCellProps) => {
    return (
      <td style={{ whiteSpace: 'nowrap' }}>
        <div style={{ float: 'left' }}>
          <button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary" onClick={() => props.enterEdit(props.dataItem)}>
            Edit
          </button>
        </div>
        <div style={{ float: 'left', paddingLeft: '10px' }}>
          <button
            style={{ float: 'left', paddingLeft: '10px' }}
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
            onClick={() => props.redirectToSCProfile(props.dataItem)}
          >
            Smart Connect Profile
          </button>
        </div>
        <div style={{ float: 'left', paddingLeft: '10px' }}>
          <button className="k-button k-button-md k-rounded-md" onClick={() => props.confirmDelete(props.dataItem)}>
            Delete
          </button>
        </div>
      </td>
    );
  };

  const MyEditCommandCell = (props: GridCellProps) => <EditCommandCell {...props} enterEdit={enterEdit} redirectToSCProfile={redirectToSCProfile} confirmDelete={confirmDelete} />;
  return (
    <PageContainerLayout pageHeaderTitle="Users">
      <Box>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <div>
            <Grid style={{ maxHeight: '550px' }} data={data} scrollable="scrollable">
              <GridColumn field="userName" title="User Name" width={200} />
              <GridColumn field="email" title="Email" width={250} />
              <GridColumn field="firstName" title="First Name" width={150} />
              <GridColumn field="lastName" title="Last Name" width={150} />
              <GridColumn field="title" title="Job Title" width={200} />
              <GridColumn field="active" title="Active" width={90} />
              <GridColumn field="hasSmartConnectProfile" title="Smart Connect" width={90} />
              <GridColumn cells={{ data: MyEditCommandCell }} width={350} />
              <GridColumn field="" title="" width="" filterable={false} columnMenu={null} />
            </Grid>
            {openForm && <UserEditForm isOpen={openForm} onCancel={handleCancel} user={editUser} title={`Edit ${editUser.userName}`} onLoadUsers={loadUsers} />}
            {openDelete && (
              <ConfirmDelete
                isOpen={openDelete}
                cancelEdit={handleCancel}
                onSubmit={handleDelete}
                item={editUser}
                title={`Delete ${editUser.userName}`}
                confirmText={`Are you sure you want to Delete: ${editUser.userName}?`}
                confirmField="userName"
              />
            )}
          </div>
        )}
      </Box>
    </PageContainerLayout>
  );
};

export default WebUsers;
